import type { MetaFunction, LoaderFunctionArgs } from 'react-router'
import { useLoaderData, Link } from 'react-router'
import { Navbar } from '~/components/navbar'
import { Footer } from '~/components/footer'
import { AUTHORS, AuthorCard, AuthorSchema } from '~/components/eeat-author'
import { BreadcrumbSchema } from '~/components/structured-data'
import { STATIC_PAGE_HEADERS } from '~/lib/cache.server'
import { db } from '@afficheservices/db'
import { blogPosts } from '@afficheservices/db/schema'
import { eq, desc } from 'drizzle-orm'

export function headers() { return STATIC_PAGE_HEADERS }

export const meta: MetaFunction<typeof loader> = ({ data }) => [
  { title: `${data?.author?.name ?? 'Auteur'} — Expert AfficheServices` },
  { name: 'description', content: `${data?.author?.name} — ${data?.author?.role}. Articles et conseils d'expert sur les travaux et la rénovation.` },
]

export async function loader({ params }: LoaderFunctionArgs) {
  const slug = params.slug ?? ''
  const author = AUTHORS[slug]
  if (!author) throw new Response('Auteur non trouvé', { status: 404 })

  // Get articles by this author (using tag matching for now)
  const articles = await db.select({
    title: blogPosts.titleFr,
    slug: blogPosts.slug,
    excerpt: blogPosts.excerptFr,
    publishedAt: blogPosts.publishedAt,
    coverImage: blogPosts.coverImageUrl,
  }).from(blogPosts)
    .where(eq(blogPosts.status, 'published'))
    .orderBy(desc(blogPosts.publishedAt))
    .limit(12)

  return { author, articles, slug }
}

export default function AuthorPage() {
  const { author, articles, slug } = useLoaderData<typeof loader>()

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <BreadcrumbSchema items={[
        { name: 'Accueil', url: 'https://www.afficheservices.fr/' },
        { name: 'Auteurs', url: 'https://www.afficheservices.fr/blog' },
        { name: author.name, url: `https://www.afficheservices.fr/auteur/${slug}` },
      ]} />
      <AuthorSchema author={author} />

      <main className="mx-auto max-w-4xl px-4 py-12">
        <AuthorCard author={author} />

        <div className="mt-6 rounded-xl bg-slate-50 p-5 text-sm text-slate-600 leading-relaxed">
          <p>
            {author.name} est {author.role.toLowerCase()} chez AfficheServices.
            {author.yearsExperience && ` Avec ${author.yearsExperience} ans d'expérience dans le secteur du bâtiment et de la rénovation,`}
            {' '}il/elle rédige des guides pratiques et des analyses de prix pour aider les particuliers dans leurs projets de travaux.
            Chaque article est vérifié par notre équipe d'experts pour garantir l'exactitude des informations et des prix indiqués.
          </p>
        </div>

        <h2 className="mt-10 text-xl font-bold text-slate-900">Articles rédigés</h2>

        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          {articles.map((article) => (
            <Link
              key={article.slug}
              to={`/blog/${article.slug}`}
              className="group rounded-xl border border-slate-200 bg-white p-4 transition-all hover:border-primary/30 hover:shadow-sm"
            >
              <h3 className="text-sm font-semibold text-slate-900 group-hover:text-primary line-clamp-2">{article.title}</h3>
              {article.excerpt && <p className="mt-1 text-xs text-slate-500 line-clamp-2">{article.excerpt}</p>}
              {article.publishedAt && (
                <p className="mt-2 text-[10px] text-slate-400">
                  {new Date(article.publishedAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                </p>
              )}
            </Link>
          ))}
        </div>
      </main>
      <Footer />
    </div>
  )
}
